import type { SiteId } from './data/sites';
import { REEF_SURVEYS } from './data/fish/reefSurveys';

/**
 * Three of Maui Nui's reef animals the game counts and does not simulate (M217, M222, M223): each as NOAA's divers counted
 * it at Maui Nui (`REEF_SURVEYS`, `scripts/reefSurveys.ts`, MEASURED) and as its sources describe it, its numbers
 * holding where no source lets the game step them without guessing (the rule: measure, never guess), as the spinner
 * dolphin's are held (M188). Each waits for what it lacks (open).
 */

/**
 * The yellow tang, *Zebrasoma flavescens*, the lauʻīpala (M217): a surgeonfish grazing the reef's algae ("Fish
 * Grazer/Detritivores", Weijerman, Fulton and Parrish 2013, Table S2, CC0). Not simulated: its growth's rate and its
 * weight by length are in Claisse et al. 2009 (*MEPS* 389) and Claisse, McTee and Parrish 2009 (*Coral Reefs* 28),
 * whose hosts refused this environment (asked of the user), and no other read gives them.
 */
export const YELLOW_TANG = {
  /** NOAA's divers' count at Maui Nui, a hectare of its hard bottom (`MAUI_NUI_HARD_BOTTOM_KM2`), the strata weighted by the game's floor (MEASURED; review M231's). */
  perHa: REEF_SURVEYS.yellowTang.perHa,
  /**
   * Its life as Schemmel 2021 quotes it (*Environ. Biol. Fish.*, NOAA's repository noaa/32596, public domain, p. 2):
   * "Settlement occurs at around 30-mm total length (TL) at approximately 62 days old ... and longevity was estimated at
   * 41 years (Claisse et al. 2009a, b)": compiled.
   */
  settlingDays: 62,
  oldestYears: 41,
  /** "L50 = 63.4-mm TL (CI: 62.7 – 65.5-mm TL)" for its females (Schemmel 2021, p. 1), MEASURED, 166 fish of Oʻahu's aquarium catch, 2016. */
  femaleHalfMatureTlMm: 63.4,
  /**
   * Its fishery, closed: "The Department of Land and Natural Resources (Department or DLNR) has not issued or renewed any
   * aquarium permits since the Supreme Court ruling in Umberger" (BLNR item F-2, 24 October 2025, p. 2).
   */
  fished: false,
} as const;

/**
 * The yellowmargin morays eel-specific baited cameras saw on the main islands' shallow reefs, Hawaiʻi, Oʻahu and Niʻihau
 * (M261: Zill 2024, PhD dissertation, University of Hawaiʻi at Mānoa, "All rights reserved", its figures cited): its 33
 * surveys, each a circle 15 m across, 5,832 m² in all (its Table S2.1), saw 41 of them (its Fig. 2.4's counts), each told apart
 * by its marks; all its morays 61 times NOAA's divers' count, and "may be underestimates", under half the rotenone
 * collections'. Its islands are not Maui Nui's; taken as the main islands' reefs' own.
 */
const MORAY_CAMERA = { surveys: 33, surveyedM2: 5832, seen: 41 } as const;

/**
 * The yellowmargin moray, *Gymnothorax flavimarginatus*, the puhi paka (M222): "the most numerous of the larger muraenids
 * in Kona" (Hobson 1974, *Fishery Bulletin* 72: 915, p. 927, public domain). Not simulated: no growth, age, maturity,
 * spawning or larval figure for it was found in any source this environment could read (M222's reader).
 */
export const MORAY = {
  /**
   * NOAA's divers' count at Maui Nui, a hectare of its hard bottom (MEASURED; review M231's): the morays out of their
   * holes, the ones the Reef draws swimming; divers under-count morays in their holes.
   */
  perHa: REEF_SURVEYS.moray.perHa,
  /** The cameras' count (M261, `MORAY_CAMERA`), and its yellowmargin morays a hectare, in their holes and out: 41 in 5,832 m², 70.3, a floor still, nearer the whole. */
  camera: MORAY_CAMERA,
  cameraPerHa: (MORAY_CAMERA.seen * 10_000) / MORAY_CAMERA.surveyedM2,
  /** The divers' total lengths, cm (MEASURED): their mean. */
  meanTlCm: REEF_SURVEYS.moray.meanTlCm,
  /**
   * Its predators the game holds: the ulua, morays in 6.2% of its guts with prey it could name and 4.9% of their prey's
   * volume (Sudekum et al. 1991, *Fishery Bulletin* 89: 493, Table 3, p. 501, public domain, the Northwestern islands);
   * the monk seal, morays in 30.8% of its main islands' samples (Cahoon et al. 2013, its abstract alone read).
   */
  eatenBy: ['ulua', 'monkSeal'] as const,
} as const;

/**
 * The reef manta, *Mobula alfredi* (M223, the manta chosen over the spotted eagle ray: the one of the two measured at
 * Maui Nui, its counts, size, seasons and hours, M223's reader; the eagle ray's no Hawaiian study). Not simulated as a
 * population: "The population of manta rays utilizing the Maui aggregation site consisted of at least 290 individuals, and
 * the rate of new individuals shows no sign of leveling off" (Deakos 2010, PhD dissertation, University of Hawaiʻi, p.
 * 69, its facts cited); "around 600 unique individuals in Maui Nui since 2005" (NOAA Fisheries' feature story, 2023, a
 * US government work): counts of animals photographed, neither an estimate of its numbers (open).
 */
export const MANTA = {
  /** Animals NOAA's photographs have told apart at Maui Nui since 2005 (NOAA Fisheries, 2023): a floor, not a count. */
  individualsSeen: 600,
  /** The share of NOAA's divers' site visits that met one (MEASURED, `REEF_SURVEYS`). */
  visitsSeenShare: REEF_SURVEYS.manta.seenShare,
  /** Its protection: "Unlawful to capture or entangle any shark or ray, whether alive or dead, or kill any shark or ray" (DAR's rules booklet, 2025, p. 8). */
  protected: true,
  /**
   * Its disc's width, m, each sex's mean at Maui (MEASURED, paired lasers: "females larger on average (mean = 3.18 m DW,
   * SD = 0.31) than males (mean = 2.83 m DW, SD = 0.14)", Deakos 2010, p. 106), and the females' share of the 154 measured,
   * "71 (46%) were females, and 83 (54%) were males" (p. 40). M242.
   */
  discM: { female: 3.18, male: 2.83 },
  femaleShare: 71 / 154,
  /**
   * Each sex's disc, m, at maturity (Deakos 2010, p. 106): "a DW of 3.37 m was a conservative estimate of the size at
   * sexual maturity" of the females, by pregnancy; "Males appeared to achieve sexual maturity between 2.75 and 2.80 m DW".
   */
  matureDiscM: { female: 3.37, male: [2.75, 2.8] },
  /**
   * Its gathering place off west Maui (Deakos 2010): "a depth range of 5 - 30 m" (p. 52), its cleaning stations where they
   * are "soliciting predominantly Hawaiian cleaner wrasses (Labroides phthirophagus) and saddle wrasses (Thalassoma
   * duperrey) to remove parasitic copepods" (p. 24); "they were never seen feeding while in the study area" (p. 72).
   */
  siteDepthM: [5, 30],
  /**
   * The rays a survey's hour at Maui's aggregation site by the hour it began (MEASURED, 229 surveys 2005 to 2009): "The
   * sighting rate during AM surveys was 1.40 manta rays per hour (SD = 3.24), 4.17 (SD = 4.25) for MIDDAY surveys, and 6.94
   * (SD = 5.16) for PM surveys" (Deakos 2010, p. 64), its bins "AM" (6:00 – 10:00), "MIDDAY" (10:00 – 14:00) and "PM"
   * (14:00 – 18:00) (p. 57); "usually absent at first light with numbers increasing throughout the day" (abstract). The two
   * tagged "moved offshore and out of the study area after sunset" (p. 72), two rays only. M242.
   */
  sightingsPerHour: [
    { fromHour: 6, toHour: 10, rate: 1.4 },
    { fromHour: 10, toHour: 14, rate: 4.17 },
    { fromHour: 14, toHour: 18, rate: 6.94 },
  ],
} as const;

/** Whether a site has the three as read: Maui Nui, where NOAA's divers counted them. */
export const siteHasReefResidents = (siteId: SiteId): boolean => siteId === 'maui_nui';
